import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  Stack,
  SwipeableDrawer,
  Typography,
  useTheme,
} from '@mui/material';
import { captureException } from '@sentry/react';
import posthog from 'posthog-js';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  AppAdaptedTooltip,
  ExternalLinkIcon,
  PortalTheme,
} from '@frontend-libs/portal';

import { siteUnitClient } from '@portal-clients/site-unit';

import { useCountVoucherQuery } from '@frontend/data';
import { useCountNewsQuery } from '@frontend/data/use-news-query';
import { useBrowser, useClientQuery } from '@frontend/hooks';
import { useLicenses } from '@frontend/licenses';
import { tApp, tAuth } from '@frontend/locales';
import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';
import {
  routes,
  useMetadata,
  useOptionalPortalConfiguration,
  useOptionalUser,
} from '@frontend/utils';

import { LifebuoyIcon } from '../icons';
import { MenuActions } from './menu-actions';

interface DrawerListItemButtonProps {
  eventId: string;
  isActive: boolean;
  onClick(): void;
  icon: ReactNode;
  title: string;
  href?: string;
  disabled?: boolean;
  ariaLabel?: string;
  tooltipTitle?: string;
  count?: number;
}

const DrawerListItemButton = (props: DrawerListItemButtonProps) => {
  const theme = useTheme();

  return (
    <AppAdaptedTooltip title={props.tooltipTitle}>
      <ListItemButton
        data-eventid={props.eventId}
        component={props.href ? 'a' : 'button'}
        {...(props.href && {
          target: '_blank',
          rel: 'noopener noreferrer',
          href: props.href,
        })}
        sx={{
          width: '100%',
          ...(props.isActive && {
            color: theme.palette.primary.main,
          }),
          py: 1,
          px: 2,
        }}
        onClick={(
          e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
        ) => {
          if (props.href) {
            e.preventDefault();
          }

          props.onClick();
        }}
        disabled={props.disabled}
        aria-label={props.ariaLabel}
      >
        <ListItemIcon>
          <Badge
            badgeContent={props.count}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `2px solid ${theme.palette.background.paper}`,
              },
            }}
          >
            {props.icon}
          </Badge>
        </ListItemIcon>
        <ListItemText
          primary={props.title}
          data-eventid={props.eventId}
          slotProps={{
            primary: {
              sx: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            },
          }}
        />
      </ListItemButton>
    </AppAdaptedTooltip>
  );
};

interface AppDrawerProps {
  openState: boolean;
  toggleDrawer(state: boolean): void;
}

export const AppDrawer = (props: AppDrawerProps) => {
  const locationPath = useLocation().pathname;
  const theme = useTheme<PortalTheme>();
  const { user } = useOptionalUser();
  const {
    content: publicContent,
    features: publicFeatures,
    portalId,
  } = usePortalPublicConfiguration();
  const config = useOptionalPortalConfiguration();
  const navigate = useNavigate();
  const { open } = useBrowser();
  const { appVersion, bundleVersion } = useMetadata();
  const metadata = useMetadata();
  const isNativeApp = Capacitor.isNativePlatform();

  const externalLinks = useMemo(() => {
    return config?.content?.externalLinks ?? publicContent?.externalLinks ?? [];
  }, [publicContent, config]);

  const metaDataString = useMemo(() => {
    return Object.entries(metadata)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${key}: ${value}`)
      .join('%0A');
  }, [metadata]);

  useEffect(() => {
    if (props.openState && Capacitor.getPlatform() === 'android') {
      const listener = App.addListener('backButton', () => {
        props.toggleDrawer(false);
      });

      return () => {
        listener
          .then((handle) => handle.remove())
          .catch((error) => {
            console.error(error);
            captureException(error);
          });
      };
    }

    return undefined;
  }, [props.openState, props.toggleDrawer]);

  const { displayName, initials } = useMemo(() => {
    if (!user) {
      return { displayName: undefined, initials: undefined };
    }

    let name: string = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    let ini: string = [user.firstName, user.lastName]
      .map((n) => n?.[0] ?? '')
      .join('')
      .trim()
      .toUpperCase();

    if (name.length === 0) {
      // Since the email is used in the portal to login we assume
      // one always exist.
      name = user.email!;
      ini = user.email![0]!;
    }

    return {
      displayName: name,
      initials: ini,
    };
  }, [user]);

  const { data: siteUnits } = useClientQuery({
    queryKey: ['site-units'],
    onFetch: async () => {
      return await siteUnitClient.search();
    },
  });

  const isActive = (route: string) => {
    if (route === '/') {
      return locationPath === route;
    }

    return locationPath.startsWith(route);
  };

  const {
    news: newsLicense,
    vouchers: vouchersLicense,
    stampCards: stampCardsLicense,
  } = useLicenses();

  const isNewsEnabled = useMemo(() => {
    return newsLicense && user
      ? (config?.features?.news?.enabled ?? false)
      : (publicFeatures?.news?.enabled ?? false);
  }, [user, publicFeatures, config]);

  const isHomeEnabled = useMemo(() => {
    return user
      ? (config?.features?.home?.enabled ?? false)
      : (publicFeatures?.home?.enabled ?? false);
  }, [user, publicFeatures, config]);

  const { data: newsCount } = useCountNewsQuery({
    onError() {
      captureException('Failed to fetch news count', { level: 'warning' });
    },
  });

  const { data: voucherCount } = useCountVoucherQuery({
    onError() {
      captureException('Failed to fetch voucher count', { level: 'warning' });
    },
  });

  const actions = useMemo(() => {
    return MenuActions({
      siteUnits,
      locationPath,
      theme,
      user,
      config,
      content: config?.content ?? publicContent,
      newsLicense,
      vouchersLicense,
      stampCardsLicense,
      isNewsEnabled,
      newsCount: newsCount?.count ?? 0,
      voucherCount: voucherCount?.count ?? 0,
      isHomeEnabled,
    });
  }, [
    siteUnits,
    locationPath,
    theme,
    user,
    config,
    publicContent,
    newsLicense,
    vouchersLicense,
    stampCardsLicense,
    isNewsEnabled,
    newsCount,
    voucherCount,
    isHomeEnabled,
  ]);

  return (
    <SwipeableDrawer
      open={props.openState}
      onOpen={() => props.toggleDrawer(true)}
      onClose={() => props.toggleDrawer(false)}
      slotProps={{
        paper: {
          sx: {
            pt: 'var(--safe-area-inset-top)',
            pb: `max(var(--safe-area-inset-bottom), ${theme.spacing(4)})`,
            minWidth: '280px',
            width: '80vw',
            maxWidth: '320px',
          },
        },
      }}
      anchor="left"
      disableSwipeToOpen={true}
    >
      <Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <ListItemButton
            data-testid="drawer-user-profile-button"
            component="a"
            onClick={() => {
              if (!user && isActive(routes.welcome)) {
                props.toggleDrawer(false);
                return;
              }

              navigate(user ? routes.profile.profile : routes.welcome);
            }}
            sx={{
              padding: 2,
            }}
          >
            <Avatar>
              {user && <Typography variant="subtitle1">{initials}</Typography>}
            </Avatar>
            <Typography ml={1.5} variant="body1">
              {user ? displayName : tAuth.actions.signUpOrLogIn()}
            </Typography>
          </ListItemButton>
        </Stack>

        <Divider />

        <List component="nav">
          {actions.map((action, index) => (
            <DrawerListItemButton
              key={index}
              disabled={action.disabled}
              eventId={`app-drawer-${action.title.toLowerCase()}`}
              isActive={action.isActive}
              onClick={() => {
                if (action.isActive) {
                  props.toggleDrawer(false);
                  return;
                }

                navigate(action.navigateTo);
              }}
              icon={action.getIcon()}
              title={action.title}
              ariaLabel={`app-drawer-${action.ariaLabelSuffix}`}
              count={action.count}
            />
          ))}
        </List>

        {externalLinks.length > 0 && (
          <>
            <Divider variant="middle" />

            <List>
              {externalLinks.map((link, index) => {
                return (
                  <ListItem disablePadding key={index} sx={{ minHeight: 0 }}>
                    <DrawerListItemButton
                      isActive={false}
                      eventId="external-link-app"
                      href={link.url}
                      onClick={() => {
                        if (link.id) {
                          posthog.capture('external_link_viewed', {
                            data_id: link.id,
                            scope_ids: [portalId],
                          });
                        }

                        open({ url: link.url });
                      }}
                      icon={<ExternalLinkIcon />}
                      title={link.title ?? ''}
                      ariaLabel={
                        isNativeApp
                          ? tApp.externalLinks.tooltips.openLinkInApp()
                          : tApp.externalLinks.tooltips.openInNewTab({
                              dest:
                                link.title ??
                                tApp.externalLinks.tooltips.externalLink(),
                            })
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Stack>
      <Stack px={2} sx={{ height: '100%' }} justifyContent="end">
        <Box
          sx={{ backgroundColor: theme.palette.grey[100], borderRadius: '6px' }}
          p={1.5}
        >
          {publicContent?.supportEmail && (
            <>
              <Stack
                direction="row"
                sx={{
                  width: '100%',
                }}
              >
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="start"
                  spacing={0.5}
                  component="address"
                  sx={{ fontStyle: 'inherit', width: '100%' }}
                >
                  <Typography variant="subtitle2" component="p">
                    {tApp.drawer.needHelp()}
                  </Typography>
                  <MuiLink
                    data-eventid="support-email"
                    color="textSecondary"
                    variant="body2"
                    component="a"
                    underline="none"
                    type="email"
                    href={`mailto:${publicContent?.supportEmail}?subject=Support for Guest App&body=%0A%0A%0A---------%0A${metaDataString}`}
                  >
                    {publicContent?.supportEmail}
                  </MuiLink>
                </Stack>
                <Stack justifyContent="start" alignItems="center">
                  <LifebuoyIcon stroke={theme.palette.grey[500]} />
                </Stack>
              </Stack>

              <Divider sx={{ width: '100%', mt: 1, mb: 1 }} />
            </>
          )}

          <Stack spacing={2} direction="row" justifyContent="space-between">
            <Typography
              variant="subtitle3"
              color="textSecondary"
              sx={{
                textTransform: 'uppercase',
                minWidth: '11em',
              }}
            >
              {tApp.app.version()}
            </Typography>
            <Typography
              align="right"
              variant="subtitle3"
              color="textSecondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
                whiteSpace: 'nowrap',
              }}
            >
              {appVersion}
            </Typography>
          </Stack>

          <Stack
            spacing={2}
            direction="row"
            justifyContent="space-between"
            pt={0.5}
          >
            <Typography
              variant="subtitle3"
              color="textSecondary"
              sx={{ textTransform: 'uppercase', minWidth: '11em' }}
            >
              {tApp.app.softwareVersion()}
            </Typography>
            <Typography
              align="right"
              variant="subtitle3"
              color="textSecondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
                whiteSpace: 'nowrap',
              }}
            >
              {bundleVersion}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </SwipeableDrawer>
  );
};
